<template lang="pug">
.public-event-tab
  .mb-3
  EventTable(
    v-model="_items"
    :fields="fields"
    :totalRows.sync="_rows"
    :currentPage.sync="currentPage"
    :perPage="perPage"
    :isLoading="isLoading"
    :filter="filter"
    @onStatusSwitch="onStatusSwitch"
    @onEdit="onEdit"
    @onDelete="onDelete"
    @onPreview="onPreview"
    @onApplicationStatus="onApplicationStatus"
    @onLiveStart="onLiveStart"
  )

  .table-options.d-flex.flex-row.justify-content-end.align-items-center.flex-wrap
    //- 検索
    .search-filter.d-flex.mr-1.align-items-center.mr-2
      b-input-group.search-filter-input-group.mr-1(size="md")
        b-input-group-prepend
          b-input-group-text.search-filter-icon
            .search-icon
        b-form-input.search-filter-input(v-model="filter.searchWord" type="search" placeholder="検索ワードを入力してください")
      HelpIcon.d-flex.justify-content-end.align-items-center(:popoverId="'event-list-nonpublic-search-filter-help'" title="検索項目について")
        template(v-slot:text)
          div 検索項目は以下になります。
          div ・タイトル
    //- ページあたりの行数
    span.table-option-text 1ページの行数:
    .per-page
      b-form-select(
        id="per-page-select"
        v-model="perPage"
        :options="pageOptions"
        size="md"
        @change="selectPerPage"
      )
    //- ページネーション
    b-pagination.m-0(
      v-model="currentPage"
      :total-rows="_rows"
      :per-page="perPage"
      :disabled="isLoading"
      @change="selectPage"
      hide-goto-end-buttons
    )
</template>

<script lang="ts">
import { Component, Prop, Watch, Vue } from "vue-property-decorator";

import Setting from "../../../../public/setting.json";

import HelpIcon from "@/pc/basics/HelpIcon.vue";
import EventTable, { EventTableField, EventTableItem, FilterSetting } from "@/pc/containers/events/EventTable.vue";

/**
 * ### 非公開のみタブコンポーネント
 */
@Component({
  components: {
    HelpIcon,
    EventTable
  }
})
export default class NonPublicEventTab extends Vue {
  /** 読込み中か否か */
  @Prop()
  isLoading!: boolean;
  /** 行情報 */
  @Prop()
  items!: Array<EventTableItem>;
  /** ページングの全体行数 */
  @Prop()
  rows!: number;

  /** 列名 */
  fields: Array<EventTableField> = [];
  /** フィルタリング条件 */
  filter: FilterSetting = { searchWord: "" };
  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** 現在取得している件数 */
  currentRows: number = 0;
  /** 1ページに表示する行数 */
  perPage: number = Number(Setting.event.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.event.pageOptions;

  get _items(): Array<EventTableItem> {
    return this.items;
  }

  get _rows(): number {
    return this.rows;
  }

  set _rows(newValue: number) {
    this.$emit("update:totalRows", newValue);
    return;
  }

  /**
   * ### 1ページあたりの表示行数の変更イベント
   *
   * - 変更をクッキーに持たせる
   */
  @Watch("perPage")
  public changePerPage(perPage: number): void {
    this.setCookie(perPage);
    return;
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  public setCookie(perPage: number): void {
    // Cookie有効秒数。有効期限は必ず設定しないといけないので、10年分にしておく
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `event-list-private-per-page=${perPage}; max-age=${saveSecond};`;
    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const fields: Array<EventTableField> = this.createFields();
    // 1ページあたりの表示行数はCookieから取得する。取得できない場合はデフォルト値を使用する
    const perPage: number | null = this.loadCookie();
    this.perPage = perPage ? perPage : Number(Setting.event.perPage);
    this.fields = fields;

    return;
  }

  /**
   * ### 列名を作成する
   */
  public createFields(): Array<EventTableField> {
    let fields: Array<EventTableField> = [];

    fields.push({ key: "publishing", label: "公開", sortable: false, sortDirection: "desc", class: "publishing" });
    fields.push({ key: "date", label: "日付", sortable: false, sortDirection: "desc", class: "date text-center" });
    fields.push({ key: "title", label: "タイトル", sortable: false, sortDirection: "desc", class: "title" });
    fields.push({ key: "targets", label: "公開先", sortable: false, sortDirection: "desc", class: "targets" });
    fields.push({ key: "preview", label: "内容", sortable: false, sortDirection: "desc", class: "preview text-center" });
    fields.push({ key: "operation", label: "登録情報", sortable: false, sortDirection: "desc", class: "operation text-center" });

    return fields;
  }

  /**
   * ### Cookieを見て、値が保持されている場合はCookieの値を返す
   */
  public loadCookie(): number | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });

      // cookieに値が格納されていないとundefinedが格納されているが、実際にはゴミデータが格納されているのでisNaN()でチェックが必要
      const perPage: number = Number(cookieMap["event-list-private-per-page"]);

      return isNaN(perPage) ? null : perPage;
    }

    return null;
  }

  /**
   * ### 1ページあたりの表示行数変更イベント
   */
  public async selectPerPage(perPage: number): Promise<void> {
    this.perPage = perPage;
    this.currentPage = 1;
    return;
  }

  /**
   * ### ページネーションのページ番号押下
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;
    return;
  }

  /**
   * ### 公開/非公開のスイッチ押下
   */
  public onStatusSwitch(item: EventTableItem): void {
    this.$emit("onStatusSwitch", item);
    return;
  }

  /**
   * ### 修正ボタン押下
   */
  public onEdit(item: EventTableItem): void {
    this.$emit("onEdit", item);
    return;
  }

  /**
   * ### 削除ボタン押下
   */
  public onDelete(item: EventTableItem): void {
    this.$emit("onDelete", item);
    return;
  }

  /**
   * ### 画像表示ボタン押下
   */
  public onPreview(item: EventTableItem): void {
    this.$emit("onPreview", item);
    return;
  }

  /**
   * ### 申込状況確認ボタン押下
   */
  public onApplicationStatus(item: EventTableItem): void {
    this.$emit("onApplicationStatus", item);
    return;
  }

  /**
   * ### 配信開始ボタン押下
   */
  public onLiveStart(item: EventTableItem): void {
    this.$emit("onLiveStart", item);
    return;
  }
}
</script>
